import { uniq } from "lodash";
import type { VariableDB } from "@hightouch/lib/customer-data/decision-engine/types";
import type { DecisionEngineDestinationDefinition } from "./definition";
import { getMappingForVariable } from "./sync-config-helpers";

/**
 * Per-slot variable mapping. Parses `hightouch_recommendation_${slot}` (the
 * indexed JSON column produced by the multi-slot resolver query) and pulls
 * the named variable from its `variables` block.
 */
const getSlottedVariableMapping = (
  variable: string,
  slot: number,
  to: string,
) => ({
  type: "template" as const,
  template: `{% assign parsedRecs = row['hightouch_recommendation_${slot}'] | parse %}{{ parsedRecs['variables']['${variable}'] }}`,
  to,
});

/**
 * Builds field mappings for an onsite surface syncing to PAPI.
 *
 * For single-slot surfaces (max_slots=1):
 *   {placement_id}_experience_id, {placement_id}_interaction_group_id,
 *   experiment_group, and one {placement_id}_{variable} per message variable.
 *
 * For multi-slot surfaces (max_slots>1):
 *   {placement_id}_experience_{N}, {placement_id}_interaction_group_id,
 *   experiment_group, and one {placement_id}_{variable}_{N} per slot per
 *   message variable. PAPI records are flat suffixed because each slot
 *   decision is stored as a separate interaction and can't be combined into
 *   a structured object in the sync.
 *
 * The resolver populates the `id` column with interaction_group_id for surfaces
 * (one record per impression, fanned across N slot rows in the warehouse).
 */
function buildOnsiteMappings(
  placementId: string,
  maxSlots: number,
  messageVariables: VariableDB[],
): Array<Record<string, string>> {
  const mappings: Array<Record<string, string>> = [
    {
      type: "standard",
      from: "experiment",
      to: "experiment_group",
    },
    {
      type: "standard",
      from: "id",
      to: `${placementId}_interaction_group_id`,
    },
  ];

  const variableNames = uniq(messageVariables.map((v) => v.name));

  if (maxSlots === 1) {
    mappings.push({
      type: "standard",
      from: "message_id",
      to: `${placementId}_experience_id`,
    });
    for (const name of variableNames) {
      mappings.push(getMappingForVariable(name, `${placementId}_${name}`));
    }
  } else {
    for (let i = 0; i < maxSlots; i++) {
      mappings.push({
        type: "standard",
        from: `message_id_${i}`,
        to: `${placementId}_experience_${i}`,
      });
      for (const name of variableNames) {
        mappings.push(
          getSlottedVariableMapping(name, i, `${placementId}_${name}_${i}`),
        );
      }
    }
  }

  return mappings;
}

export const personalizationApiDefinition: DecisionEngineDestinationDefinition =
  {
    destinationType: "hightouch-personalization-api",
    producers: ["aid"],
    category: "batch",

    generateSyncConfig: ({ scopeId, settings, messageVariables = [] }) => {
      const placement = settings.placement;
      if (!placement) {
        throw new Error(
          "Onsite channel is missing placement config for PAPI sync",
        );
      }

      const placementId = placement.placement_id;
      const maxSlots = placement.max_slots ?? 1;
      const collection = `onsite-${scopeId}`;

      return {
        mode: "upsert",
        dataset: collection,
        object: collection,
        deleteMode: "clear",
        autoSyncColumns: false,
        externalIdMapping: { from: "external_id", to: "id" },
        mappings: buildOnsiteMappings(placementId, maxSlots, messageVariables),
      };
    },

    syncStrategy: {
      supportsChannelLevelSync: true,
      shouldManageSyncs: true,
      getSyncSchedule: () => ({
        type: "cron",
        schedule: { expression: "0 * * * *" },
      }),
    },
  };
